import React, { useEffect, useMemo, useState } from 'react';
import {
  ChakraProvider,
  theme,
  Center,
  Spinner,
} from '@chakra-ui/react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import DashboardLayout from './components/DashboardLayout';
import DashboardHome from './pages/DashboardHome';
import Templates from './pages/Templates';
import Messages from './pages/Messages';
import Billing from './pages/Billing';
import VerifyEmail from './pages/VerifyEmail';
import ForgotPassword from './pages/ForgotPassword';
import PasswordReset from './pages/PasswordReset';
import Terms from './pages/Terms';
import { useKeycloak } from '@react-keycloak/web';
import getVerificationServiceConfig from './util/verificationService';

// Guard: blocks routes when Keycloak user isn't email-verified
function EmailVerifiedGuard({ element }) {
  const { keycloak } = useKeycloak();
  const location = useLocation();
  const qs = useMemo(() => new URLSearchParams(location.search), [location.search]);
  const bypassOnce = qs.get('verified') === '1';
  const [emailVerified, setEmailVerified] = useState(null); // null | true | false

  useEffect(() => {
    const claim =
      (keycloak.idTokenParsed && keycloak.idTokenParsed.email_verified) ??
      (keycloak.tokenParsed && keycloak.tokenParsed.email_verified);

    if (typeof claim === 'boolean') {
      setEmailVerified(claim);
      return;
    }

    let cancelled = false;
    keycloak
      .loadUserInfo() // requires "email" scope in Keycloak client
      .then((info) => {
        if (!cancelled) setEmailVerified(Boolean(info && info.email_verified));
      })
      .catch(() => {
        if (!cancelled) setEmailVerified(false);
      });

    return () => {
      cancelled = true;
    };
  }, [keycloak]);

  if (bypassOnce) return element;
  if (emailVerified === null) {
    return (
      <Center h="100vh"><Spinner size="xl" /></Center>
    );
  }
  return emailVerified ? element : <Navigate to="/verify-email" replace />;
}

function App() {
  const { keycloak, initialized } = useKeycloak();
  const { defaultRedirectPath } = useMemo(() => getVerificationServiceConfig(), []);
  const publicRoutes = useMemo(
    () => ['/forgot-password', '/terms', defaultRedirectPath].filter(Boolean),
    [defaultRedirectPath]
  );
  const currentPath = typeof window !== 'undefined' ? window.location.pathname : '';
  const isPublicRoute = publicRoutes.some((path) =>
    currentPath === path || currentPath.startsWith(`${path}/`)
  );

  if (!initialized) {
    return (
      <ChakraProvider theme={theme}>
        <Center h="100vh"><Spinner size="xl" /></Center>
      </ChakraProvider>
    );
  }

  if (!isPublicRoute && !keycloak.authenticated) {
    keycloak.login({ redirectUri: window.location.href });
    return null;
  }

  return (
    <ChakraProvider theme={theme}>
      <Router>
        <Routes>
          <Route path="/forgot-password" element={<ForgotPassword />} />
          <Route path={defaultRedirectPath || '/password-reset'} element={<PasswordReset />} />
          <Route path="/terms" element={<Terms />} />
          <Route element={<DashboardLayout />}>
            <Route path="/verify-email" element={<VerifyEmail />} />
            <Route path="/dashboard" element={<EmailVerifiedGuard element={<DashboardHome />} />} />
            <Route path="/dashboard/templates" element={<EmailVerifiedGuard element={<Templates />} />} />
            <Route path="/dashboard/messages" element={<EmailVerifiedGuard element={<Messages />} />} />
            <Route path="/dashboard/billing" element={<EmailVerifiedGuard element={<Billing />} />} />
            <Route path="/" element={<Navigate to="/dashboard" replace />} />
          </Route>
        </Routes>
      </Router>
    </ChakraProvider>
  );
}

export default App;
